/**
 * 我的收藏: 视频资源 / 句子 / 单词 三个 tab.
 *
 * 卡片外观与资源列表页、跟读页保持一致: 资源卡复用同一套首图 + 标题样式,
 * 句子卡复用句子的排版, 单词卡展示词头/词性/释义;
 * 三个 tab 的卡片右下角都有书签, 点了就地取消收藏。
 *
 * 进入页面与切换 tab 都会重新拉一次, 保证在别处刚收藏的内容立刻出现。
 */

import { ApiError, api } from '../lib/api'
import { FREE_BADGE_CLASS, TAG_BADGE_CLASS } from '../lib/badges'
import { courseContent, listLanguage } from '../lib/content-language'
import { ensureCourseAccess } from '../lib/entitlement'
import { courseFavorites } from '../lib/favorites'
import { t } from '../lib/i18n'
import { rangeLabel, toSentenceViews, type SentenceView } from '../lib/lesson'
import { goTo } from '../stores/router'
import { toast } from '../stores/toast'
import {
  WORD_FAVORITE_CHANGED,
  type WordCardStore,
  type WordFavoriteChangedDetail,
} from '../stores/word-card'
import type {
  Course,
  FavoriteCourse,
  FavoriteSentence,
  FavoriteTarget,
  FavoriteWord,
  Sentence,
} from '../types/api'

type TabKey = 'course' | 'sentence' | 'word'

interface TabView {
  key: TabKey
  label: string
  disabled: boolean
}

interface SentenceItem {
  view: SentenceView
  courseId: number
  courseTitle: string
  courseTitleTranslation: string
}

/**
 * 收藏句子的 id 字段叫 sentence_id, 先对齐成 Sentence 再交给 toSentenceViews.
 * 直接用的话生成的视图 id 是 undefined, x-for 的 key 重复会互相覆盖,
 * 收藏页就只显示一条(且不是最新收藏的那条).
 */
function toSentence(item: FavoriteSentence): Sentence {
  return {
    id: item.sentence_id,
    position: item.position,
    marker: item.marker,
    text: item.text,
    translation: item.translation,
    start_seconds: item.start_seconds,
    end_seconds: item.end_seconds,
  }
}

/**
 * 就地取消收藏: 先乐观从列表移除, 失败回滚.
 * 句子/单词 tab 共用, 区别只有收藏类型与列表字段, 所以由调用方传入读写方式.
 */
async function removeFavoriteItem<T>(
  targetType: FavoriteTarget,
  targetId: number,
  list: T[],
  setList: (next: T[]) => void,
  keep: (item: T) => boolean,
): Promise<void> {
  const previous = list
  setList(list.filter(keep))
  try {
    await api.favorites.remove(targetType, targetId)
    toast('已取消收藏')
  } catch (error) {
    setList(previous)
    toast(error instanceof ApiError ? error.message : '操作失败, 请重试')
  }
}

export function favoritesPage() {
  return {
    ...courseFavorites(),
    ...courseContent(),
    tab: 'course' as TabKey,
    loading: false,
    loadedTabs: [] as TabKey[],
    error: '',
    courses: [] as FavoriteCourse[],
    sentenceItems: [] as SentenceItem[],
    wordItems: [] as FavoriteWord[],

    tagClass: TAG_BADGE_CLASS,
    freeBadgeClass: FREE_BADGE_CLASS,
    rangeLabel,

    /** 单词卡弹层是共享 store: 在弹层里取消收藏后, 这里同步移除列表项 */
    init(): void {
      window.addEventListener(WORD_FAVORITE_CHANGED, (event) => {
        const detail = (event as CustomEvent<WordFavoriteChangedDetail>).detail
        if (!detail.saved) {
          this.wordItems = this.wordItems.filter((word) => word.word_id !== detail.wordId)
        }
      })
    },

    get tabs(): TabView[] {
      return [
        { key: 'course', label: t('favorites.tabCourses'), disabled: false },
        { key: 'sentence', label: t('favorites.tabSentences'), disabled: false },
        { key: 'word', label: t('favorites.tabWords'), disabled: false },
      ]
    },

    get hasLoaded(): boolean {
      return this.loadedTabs.includes(this.tab)
    },

    get isEmpty(): boolean {
      if (this.tab === 'course') return this.courses.length === 0
      if (this.tab === 'sentence') return this.sentenceItems.length === 0
      return this.wordItems.length === 0
    },

    async selectTab(key: TabKey): Promise<void> {
      const target = this.tabs.find((item) => item.key === key)
      if (!target || target.disabled || key === this.tab) return
      this.tab = key
      await this.load()
    },

    async load(): Promise<void> {
      // 每次都重新拉: 在别处刚收藏/取消的内容必须立即反映出来(进入页面与切换 tab 都会调)
      const tab = this.tab
      this.loading = true
      this.error = ''
      try {
        if (tab === 'course') {
          const page = await api.favorites.courses({ limit: 200 })
          this.courses = page.items
          await this.loadSavedCourseIds()
        } else if (tab === 'sentence') {
          const page = await api.favorites.sentences({ limit: 200 })
          const views = toSentenceViews(page.items.map(toSentence))
          this.sentenceItems = views.map((view, index) => ({
            view,
            courseId: page.items[index]?.course_id ?? 0,
            courseTitle: page.items[index]?.course_title ?? '',
            courseTitleTranslation: page.items[index]?.course_title_translation ?? '',
          }))
        } else if (tab === 'word') {
          const page = await api.favorites.words({ limit: 200 })
          this.wordItems = page.items
        }
        if (!this.loadedTabs.includes(tab)) this.loadedTabs.push(tab)
      } catch (error) {
        this.error = error instanceof ApiError ? error.message : '收藏加载失败, 请稍后重试'
      } finally {
        if (this.tab === tab) this.loading = false
      }
    },

    minutesOf(course: Course): number {
      return Math.max(1, Math.round(course.duration_seconds / 60))
    },

    /** 收藏句子卡片右上角的资源名: 中文模式取译文, 缺失时回退英文 */
    sentenceCourseTitle(item: SentenceItem): string {
      if (listLanguage() === 'chinese') {
        return item.courseTitleTranslation || item.courseTitle
      }
      return item.courseTitle
    },

    /** 收藏句子卡片是否改用中文译文做主文本(中文模式且有译文) */
    sentenceHasChinese(item: SentenceItem): boolean {
      return listLanguage() === 'chinese' && Boolean(item.view.translation)
    },

    openCourse(course: Course): void {
      if (!ensureCourseAccess(course)) return
      goTo('practice', { id: String(course.id) })
    },

    /** 收藏页里取消收藏后, 这张卡要从列表里立即消失 */
    async toggleCourse(course: Course): Promise<void> {
      const wasSaved = this.isCourseSaved(course.id)
      await this.toggleCourseSave(course)
      if (wasSaved) {
        this.courses = this.courses.filter((item) => item.id !== course.id)
      }
    },

    openSentence(item: SentenceItem): void {
      goTo('practice', { id: String(item.courseId) })
    },

    /** 点单词词条打开共享的词卡弹层(与跟读页同一个组件) */
    openWord(item: FavoriteWord): void {
      const store = window.Alpine?.store('wordCard') as WordCardStore | undefined
      void store?.show(item.word_id)
    },

    /** 取消收藏句子: 卡片随即从列表消失 */
    async removeFavoriteSentence(item: SentenceItem): Promise<void> {
      await removeFavoriteItem(
        'sentence',
        item.view.id,
        this.sentenceItems,
        (next) => {
          this.sentenceItems = next
        },
        (entry) => entry.view.id !== item.view.id,
      )
    },

    /** 取消收藏单词: 卡片随即从列表消失 */
    async removeFavoriteWord(item: FavoriteWord): Promise<void> {
      await removeFavoriteItem(
        'word',
        item.word_id,
        this.wordItems,
        (next) => {
          this.wordItems = next
        },
        (word) => word.word_id !== item.word_id,
      )
    },
  }
}

// 调用方: index.html 的 x-data="favoritesPage()"
